/**
 * Short-lived guest Face Identity session (mobile QR scan).
 * JWT is stored in sessionStorage only for the active tab (15 min).
 */

import { getSalonAdminApiBaseUrl } from '../salon-admin/utils/apiConfig';
import { fetchWithTimeout } from '../salon/utils/fetchWithTimeout';
import { getDeviceLocationHeaders } from './deviceLocationCache';
import { acquireKioskDeviceLocation } from './deviceLocationCache';
import { persistFranchiseBrand } from './franchiseBrand';

export const GUEST_FLOW_STORAGE_KEY = 'naturals_guest_flow_auth';
export const GUEST_FLOW_SESSION_MAX_MS = 15 * 60 * 1000;

function adminApiBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

function isExpired(auth) {
  if (!auth) return true;
  const started = Number(auth.signed_in_at || 0);
  if (!Number.isFinite(started) || started <= 0) return true;
  return Date.now() - started >= GUEST_FLOW_SESSION_MAX_MS;
}

export function readGuestFlowAuth() {
  try {
    const raw = sessionStorage.getItem(GUEST_FLOW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.guest_token || !parsed?.organization_id) return null;
    if (isExpired(parsed)) {
      clearGuestFlowAuth();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function hasGuestFlowAuth() {
  return Boolean(readGuestFlowAuth()?.guest_token);
}

export function persistGuestFlowAuth(payload) {
  const next = {
    guest_token: String(payload.guest_token || '').trim(),
    organization_id: String(payload.organization_id || ''),
    franchise_id: payload.franchise_id ?? null,
    franchise_name: payload.franchise_name || '',
    franchise_code: payload.franchise_code || null,
    franchise_logo: payload.franchise_logo || payload.org_logo || '',
    org_logo: payload.org_logo || payload.franchise_logo || '',
    latitude: payload.latitude ?? null,
    longitude: payload.longitude ?? null,
    geofence_radius_m: Number(payload.geofence_radius_m) || 250,
    geofence_enabled: payload.geofence_enabled === true,
    signed_in_at: Date.now(),
  };
  sessionStorage.setItem(GUEST_FLOW_STORAGE_KEY, JSON.stringify(next));
  if (next.franchise_logo || next.franchise_name) {
    persistFranchiseBrand({
      logo: next.franchise_logo || next.org_logo,
      name: next.franchise_name,
    });
  }
  return next;
}

export function clearGuestFlowAuth() {
  try {
    sessionStorage.removeItem(GUEST_FLOW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function guestAuthHeaders(extra = {}) {
  const auth = readGuestFlowAuth();
  const headers = getDeviceLocationHeaders(extra);
  if (auth?.guest_token) {
    headers.Authorization = `Bearer ${auth.guest_token}`;
  }
  return headers;
}

export function getGuestOrganizationId() {
  return readGuestFlowAuth()?.organization_id || '';
}

export function getGuestFranchiseId() {
  const raw = readGuestFlowAuth()?.franchise_id;
  return raw == null || raw === '' ? '' : String(raw);
}

async function postGuestEntry(ref, headers) {
  const base = adminApiBase();
  const body = /^[0-9]+$/.test(ref)
    ? { franchise_id: ref }
    : { franchise_code: ref };

  const res = await fetchWithTimeout(`${base}/franchise-flow/guest-entry`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    timeoutMs: 20000,
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

/**
 * Start guest session. Location is only required when the outlet has geofence enabled.
 */
export async function startGuestEntrySession(franchiseRef) {
  const base = adminApiBase();
  if (!base) throw new Error('API is not configured');

  const ref = String(franchiseRef || '').trim();
  if (!ref) throw new Error('Franchise is required');

  let headers = { 'Content-Type': 'application/json' };
  // Soft-include GPS if already granted / cached — do not force prompt yet.
  try {
    await acquireKioskDeviceLocation({ required: false, forceRefresh: false });
    headers = getDeviceLocationHeaders(headers);
  } catch {
    /* continue without location */
  }

  let { res, data } = await postGuestEntry(ref, headers);

  const needsLocation =
    !res.ok &&
    (data?.code === 'DEVICE_LOCATION_REQUIRED' ||
      data?.code === 'OUTSIDE_GEOFENCE' ||
      /location|geofence|gps/i.test(String(data?.error || '')));

  if (needsLocation) {
    await acquireKioskDeviceLocation({ required: true, forceRefresh: true });
    headers = getDeviceLocationHeaders({ 'Content-Type': 'application/json' });
    if (!headers['X-Device-Latitude'] || !headers['X-Device-Longitude']) {
      const err = new Error(
        'Location permission is required. Enable GPS and try again at the store.',
      );
      err.code = 'DEVICE_LOCATION_REQUIRED';
      throw err;
    }
    ({ res, data } = await postGuestEntry(ref, headers));
  }

  if (!res.ok || !data?.guest_token) {
    const err = new Error(data.error || 'Could not start guest check-in');
    err.code = data.code;
    throw err;
  }
  return persistGuestFlowAuth(data);
}
